import { create } from 'zustand';
import { http } from '@/libs/http';
import { toast } from 'sonner';
import type { Resource, ResourceKind, ResourceKindCreate, ResourceKindUpdate } from '@/types';

interface ResourceState {
    resources: Resource[];
    resourceKinds: ResourceKind[];
    isLoading: boolean;
    error: string | null;

    listResources: () => Promise<Resource[]>;
    listResourceKinds: () => Promise<ResourceKind[]>;
    createResourceKind: (data: ResourceKindCreate) => Promise<void>;
    updateResourceKind: (id: string, data: ResourceKindUpdate) => Promise<void>;
    deleteResourceKind: (id: string) => Promise<void>;
}

export const useResources = create<ResourceState>((set, get) => ({
    resources: [],
    resourceKinds: [],
    isLoading: false,
    error: null,

    listResources: async () => {
        set({ isLoading: true, error: null });
        try {
            const payload = await http.get<any>('/mgmt/v1/resources');
            const data = Array.isArray(payload) ? payload : payload.items || [];
            set({ resources: data, isLoading: false });
            return data;
        } catch (err: any) {
            const errorMsg = err.message || 'Failed to load resources';
            set({ error: errorMsg, isLoading: false });
            toast.error(errorMsg);
            return [];
        }
    },

    listResourceKinds: async () => {
        set({ isLoading: true, error: null });
        try {
            const payload = await http.get<any>('/mgmt/v1/resource-kinds');
            const data = Array.isArray(payload) ? payload : payload.items || [];
            set({ resourceKinds: data, isLoading: false });
            return data;
        } catch (err: any) {
            const errorMsg = err.message || 'Failed to load resource kinds';
            set({ error: errorMsg, isLoading: false });
            toast.error(errorMsg);
            return [];
        }
    },

    createResourceKind: async (data) => {
        set({ isLoading: true, error: null });
        try {
            const response = await http.post<any>('/mgmt/v1/resource-kinds', data);
            const newKind = response.data || response;
            set({
                resourceKinds: [...get().resourceKinds, newKind],
                isLoading: false,
            });
            toast.success('Resource kind created successfully');
        } catch (err: any) {
            const errorMsg = err.response?.data?.message || err.message || 'Failed to create resource kind';
            set({ error: errorMsg, isLoading: false });
            toast.error(errorMsg);
            throw err;
        }
    },

    updateResourceKind: async (id, data) => {
        set({ isLoading: true, error: null });
        try {
            const response = await http.put<any>(`/mgmt/v1/resource-kinds/${id}`, data);
            const updatedKind = response.data || response;
            set({
                resourceKinds: get().resourceKinds.map((k) => (k.id === id ? updatedKind : k)),
                isLoading: false,
            });
            toast.success('Resource kind updated successfully');
        } catch (err: any) {
            const errorMsg = err.response?.data?.message || err.message || 'Failed to update resource kind';
            set({ error: errorMsg, isLoading: false });
            toast.error(errorMsg);
            throw err;
        }
    },

    deleteResourceKind: async (id) => {
        set({ isLoading: true, error: null });
        try {
            await http.delete(`/mgmt/v1/resource-kinds/${id}`);
            set({
                resourceKinds: get().resourceKinds.filter((k) => k.id !== id),
                isLoading: false,
            });
            toast.success('Resource kind deleted successfully');
        } catch (err: any) {
            const errorMsg = err.response?.data?.message || err.message || 'Failed to delete resource kind';
            set({ error: errorMsg, isLoading: false });
            toast.error(errorMsg);
            throw err;
        }
    },
}));
